import { Box, Button, FormControl, MenuItem, TextField } from "@mui/material";
import { AppSelect, InputLabel } from "components/common/Input";
import { Formik } from "formik";

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "services/api.service";
import { readableDate } from "utils/readableDate";

type Pickup = {
  id: number;
  orderNumber: string;
  scheduledAt: string;
};

type Donation = {
  id: number;
  createdAt: string;
  donationRequest: { benefitingOrganization: { name: string } };
};

type TransactionGroup = {
  creditTransaction: string;
  pickupId: string;
  donationId: string;
  postedAt: Date;
  debitTransaction?: string;
  airbaseId?: string;
};

export function RevivnDonationMatchTransactionGroupForm() {
  const { uuid, transactionGroupId } = useParams();
  const [today] = useState(() => new Date());
  const [pickups, setPickups] = useState<Pickup[]>([]);
  const [donations, setDonations] = useState<Donation[]>([]);
  const [transactionGroup, setTransactionGroup] = useState<TransactionGroup | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/organizations/${uuid}/transaction_groups/transaction_pickups`).then(({ data }) => {
      setPickups(data.pickups);
    });
    api.get(`/organizations/${uuid}/transaction_groups/transaction_donations`).then(({ data }) => {
      setDonations(data.donations);
    });
  }, [uuid]);

  useEffect(() => {
    if (!transactionGroupId) return;
    api.get(`/organizations/${uuid}/transaction_groups/${transactionGroupId}`).then(({ data }) => {
      setTransactionGroup(data);
    });
  }, [transactionGroupId, uuid]);

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={
          transactionGroup || {
            creditTransaction: "",
            pickupId: "",
            donationId: "",
            postedAt: today,
          }
        }
        onSubmit={(values) => {
          api
            .post(`/organizations/${uuid}/transaction_groups`, {
              transactionGroup: { type: "RevivnDonationMatch", ...values },
            })
            .then(({ data }) => {
              navigate(`/finance/clients/${uuid}/transaction_groups/RevivnDonationMatch/${data.id}`, {
                replace: true,
              });
            });
        }}
      >
        {({ getFieldProps, values, handleSubmit }) => (
          <Box
            sx={{
              p: 4,
              mb: 4,
              background: "background.paper",
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
              <FormControl variant="standard" fullWidth>
                <InputLabel shrink>Credit Amount:</InputLabel>
                <TextField
                  id="name"
                  size="small"
                  {...getFieldProps("creditTransaction")}
                  disabled={!!transactionGroupId}
                />
              </FormControl>
              <FormControl variant="standard" fullWidth>
                <InputLabel shrink>Posted At:</InputLabel>
                <TextField
                  id="name"
                  size="small"
                  type={transactionGroupId ? "" : "date"}
                  {...getFieldProps("postedAt")}
                  disabled={!!transactionGroupId}
                />
              </FormControl>
              <FormControl variant="standard" fullWidth>
                <InputLabel shrink>Select associated pickup:</InputLabel>
                <AppSelect {...getFieldProps("pickupId")} disabled={!!transactionGroupId || !!values.donationId}>
                  {pickups.map((pickup) => (
                    <MenuItem key={pickup.id} value={pickup.id}>
                      {pickup.orderNumber} - {readableDate(pickup.scheduledAt)}
                    </MenuItem>
                  ))}
                </AppSelect>
              </FormControl>
              <FormControl variant="standard" fullWidth>
                <InputLabel shrink>Select associated donation:</InputLabel>
                <AppSelect {...getFieldProps("donationId")} disabled={!!transactionGroupId || !!values.pickupId}>
                  {donations.map((donation) => (
                    <MenuItem key={donation.id} value={donation.id}>
                      {donation.donationRequest.benefitingOrganization.name} - {readableDate(donation.createdAt)}
                    </MenuItem>
                  ))}
                </AppSelect>
              </FormControl>
            </Box>
            {!transactionGroupId && (
              <Box display="flex" justifyContent="flex-end" mt={6}>
                <Button variant="contained" onClick={() => handleSubmit()}>
                  Save
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Formik>
      {transactionGroupId && (
        <Formik
          enableReinitialize
          initialValues={
            transactionGroup || {
              debitTransaction: "",
              airbaseId: "",
            }
          }
          onSubmit={({ debitTransaction, airbaseId }) => {
            api
              .put(`/organizations/${uuid}/transaction_groups/${transactionGroupId}`, {
                transactionGroup: { type: "RevivnDonationMatch", debitTransaction, airbaseId },
              })
              .then(({ data }) => {
                setTransactionGroup(data);
              });
          }}
        >
          {({ getFieldProps, handleSubmit }) => (
            <Box
              sx={{
                p: 4,
                mb: 4,
                background: "background.paper",
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel shrink>Debit Amount:</InputLabel>
                  <TextField
                    id="name"
                    size="small"
                    {...getFieldProps("debitTransaction")}
                    disabled={!!transactionGroup?.debitTransaction}
                  />
                </FormControl>
                <FormControl variant="standard" fullWidth>
                  <InputLabel shrink>Airbase ID:</InputLabel>
                  <TextField
                    id="name"
                    size="small"
                    {...getFieldProps("airbaseId")}
                    disabled={!!transactionGroup?.debitTransaction}
                  />
                </FormControl>
              </Box>
              {!(transactionGroup && transactionGroup.debitTransaction) && (
                <Box display="flex" justifyContent="flex-end" mt={6}>
                  <Button variant="contained" onClick={() => handleSubmit()}>
                    Save
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Formik>
      )}
    </>
  );
}
